import type { StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';

/**
 * The engine's rules for recognising a tile source, and the address it serves one at.
 *
 * Rules, not conventions: the engine picks a tile source by media type and nothing else
 * (`source_kind.rs`), and the tile route refuses any grid but `EPSG:3857` — an archive built
 * on another matrix draws in the wrong place. How a person *describes* an asset in the admin
 * form is a separate vocabulary, and stays there.
 */

/** The media type the PMTiles v3 specification recommends, and the only one the engine reads as tiles. */
export const TILE_SOURCE_MEDIA_TYPE = 'application/vnd.pmtiles';

/** The projection of the tile grid, and so of every archive built for it. */
export const TILE_GRID_CODE = 'EPSG:3857';

/** Compared as RFC 9110 defines it: case-insensitive, and parameters after `;` ignored. */
export const isTileSourceMediaType = (declared?: string): boolean =>
    (declared ?? '').split(';')[0].trim().toLowerCase() === TILE_SOURCE_MEDIA_TYPE;

/** Whether a declared projection is the grid the tile routes actually serve. */
export const servesTilesOn = (code?: string): boolean => (code ?? '').trim().toUpperCase() === TILE_GRID_CODE;

/**
 * The archive a version's tiles are drawn from, and the layer inside it.
 *
 * MapLibre cannot draw a vector source without naming the layer within it. That name is
 * written into the archive by whatever built it, and the engine reads it back out of the
 * archive's own metadata — so it is the only source of it. A producer declaring one separately
 * could only ever disagree with what the tiles actually carry.
 */

export type TileSource = {
    /** The producer's own name for the asset. The engine reserves none. */
    assetKey: string;
    /** The layer inside the archive that a style must name. */
    layer: string;
};

/**
 * Whether a version carries an archive at all.
 *
 * A different question from [`tileSourceOf`], which also needs to know the layer: this decides
 * whether the engine is worth asking, that decides whether there is anything to draw.
 */
export const hasTileArchive = (item: StacItem | undefined): boolean =>
    Object.values(item?.assets ?? {}).some(asset => isTileSourceMediaType(asset.type));

export const tileSourceOf = (
    item: StacItem | undefined,
    /** What the engine read out of the archive, in the order the archive declared them. */
    servedLayers?: string[]
): TileSource | null => {
    const archives = Object.entries(item?.assets ?? {})
        .filter(([, asset]) => isTileSourceMediaType(asset.type))
        // Two archives cannot be ranked — the media type cannot tell vectors from images
        // (ADR-021) — so the first by key answers, rather than whatever order the map held.
        .sort(([a], [b]) => a.localeCompare(b));

    const [entry] = archives;
    if (!entry) {
        return null;
    }

    const [assetKey] = entry;
    // Only what the archive itself declares. There is nowhere else the layer could come from
    // that would not risk disagreeing with the tiles.
    const layer = servedLayers?.[0] ?? '';

    if (layer.trim().length === 0) {
        return null;
    }

    return { assetKey, layer: layer.trim() };
};

/** The engine's tile route for one version, with the placeholders MapLibre fills in. */
export const tileUrlTemplate = (engineApiUrl: string, collectionId: string, itemId: string): string => {
    const base = engineApiUrl.replace(/\/+$/, '');
    const collection = encodeURIComponent(collectionId);
    const item = encodeURIComponent(itemId);

    return `${base}/catalogue/collections/${collection}/items/${item}/tiles/{z}/{x}/{y}.mvt`;
};
